/**
 * Dynamic Data Tagging System
 *
 * This system allows for dynamic form generation based on JSON data structure
 * and provides a way to tag elements with path references that can be
 * reconstructed back to the original data path.
 */

export interface DataSchema {
  [key: string]: {
    type: 'object' | 'array' | 'text'
    label?: string
    fields?: DataSchema
    itemType?: 'object' | 'text'
  }
}

export interface FormLevel {
  level: number
  label: string
  options: FormOption[]
  selectedValue: string | number | null
  path: (string | number)[]
}

export interface FormOption {
  value: string | number
  label: string
  type: 'object' | 'array' | 'text' | 'array_index'
  preview?: string
  hasChildren?: boolean
}

export interface DataTag {
  pathComponents: (string | number)[]
  path: string
  value: any
  metadata: {
    depth: number
    type: string
    isArray: boolean
  }
}

export class DynamicPathBuilder {
  private schema: DataSchema
  private data: any
  private flattenedData: Record<string, any>

  constructor(schema: DataSchema, data: any) {
    this.schema = schema
    this.data = data
    this.flattenedData = this.flattenData(data)
  }

  private flattenData(
    obj: any,
    prefix: string = '',
    result: Record<string, any> = {},
  ): Record<string, any> {
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const newKey = prefix ? `${prefix}.${key}` : key

        if (Array.isArray(obj[key])) {
          obj[key].forEach((item: any, index: number) => {
            if (typeof item === 'object' && item !== null) {
              this.flattenData(item, `${newKey}.${index}`, result)
            } else {
              result[`${newKey}.${index}`] = item
            }
          })
        } else if (typeof obj[key] === 'object' && obj[key] !== null) {
          this.flattenData(obj[key], newKey, result)
        } else {
          result[newKey] = obj[key]
        }
      }
    }
    return result
  }

  /**
   * Analyze the current path to determine what the next dropdown should show
   */
  getNextLevelOptions(currentPath: (string | number)[] = []): FormOption[] {
    const currentData = this.navigateToPath(this.data, currentPath)
    const currentSchema = this.navigateToPath(this.schema, currentPath)

    if (!currentData) return []

    // If current data is an array, show indices
    if (Array.isArray(currentData)) {
      return currentData.map((item: any, index: number) => ({
        value: index,
        label: this.getArrayItemLabel(item, index), // Display as 1-based
        type: 'array_index' as const,
        preview: this.getPreviewText(item),
        hasChildren: this.hasChildren(item),
      }))
    }

    // If current data is an object, show its keys
    if (typeof currentData === 'object' && currentData !== null) {
      return Object.keys(currentData).map((key) => {
        const value = currentData[key]
        const fieldSchema = currentSchema?.fields?.[key]

        return {
          value: key,
          label: this.getFieldLabel(key, fieldSchema),
          type: this.getFieldType(value, fieldSchema),
          preview: this.getPreviewText(value),
          hasChildren: this.hasChildren(value),
        }
      })
    }

    return []
  }

  private navigateToPath(obj: any, pathArray: (string | number)[]): any {
    return pathArray.reduce((current: any, key: string | number) => {
      if (current === null || current === undefined) return null
      return current[key]
    }, obj)
  }

  private getArrayItemLabel(item: any, displayIndex: number): string {
    if (typeof item === 'string') {
      return `${displayIndex + 1}: ${
        item.length > 30 ? item.slice(0, 30) + '...' : item
      }`
    }
    if (typeof item === 'object' && item !== null) {
      // Try to find a meaningful label from common fields
      const labelFields = [
        'title',
        'name',
        'label',
        'q',
        'question',
        'headline',
      ]
      for (const field of labelFields) {
        if (item[field]) {
          const labelText = String(item[field])
          return `${displayIndex + 1}: ${
            labelText.length > 30 ? labelText.slice(0, 30) + '...' : labelText
          }`
        }
      }
      return `${displayIndex + 1}: Object`
    }
    return `${displayIndex + 1}: ${String(item)}`
  }

  private getFieldLabel(key: string, schema?: any): string {
    if (schema?.label) return schema.label
    // Convert snake_case and camelCase to readable format
    return key
      .split(/[_-]/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  private getFieldType(value: any, schema?: any): 'object' | 'array' | 'text' {
    if (schema?.type) return schema.type
    if (Array.isArray(value)) return 'array'
    if (typeof value === 'object' && value !== null) return 'object'
    return 'text'
  }

  private getPreviewText(value: any): string {
    if (typeof value === 'string') {
      return value.length > 50 ? value.slice(0, 50) + '...' : value
    }
    if (Array.isArray(value)) {
      return `Array (${value.length} items)`
    }
    if (typeof value === 'object' && value !== null) {
      const keys = Object.keys(value)
      return `Object (${keys.length} properties)`
    }
    return String(value)
  }

  private hasChildren(value: any): boolean {
    return Array.isArray(value) || (typeof value === 'object' && value !== null)
  }

  /**
   * Check if we can go deeper (i.e., if current selection has children)
   */
  canAddMoreLevels(currentPath: (string | number)[]): boolean {
    const currentData = this.navigateToPath(this.data, currentPath)
    return this.hasChildren(currentData)
  }

  /**
   * Check if current path points to a final value
   */
  isFinalValue(currentPath: (string | number)[]): boolean {
    const currentData = this.navigateToPath(this.data, currentPath)
    return !this.hasChildren(currentData)
  }

  /**
   * Build the complete path string
   */
  buildPathString(pathArray: (string | number)[]): string {
    return pathArray.join('.')
  }

  /**
   * Get the final value at the path
   */
  getFinalValue(pathArray: (string | number)[]): any {
    return this.navigateToPath(this.data, pathArray)
  }

  /**
   * Create a tag from the current path
   */
  createTag(pathArray: (string | number)[]): DataTag {
    const pathString = this.buildPathString(pathArray)
    const value = this.getFinalValue(pathArray)

    return {
      pathComponents: [...pathArray],
      path: pathString,
      value: value,
      metadata: {
        depth: pathArray.length,
        type: typeof value,
        isArray: Array.isArray(value),
      },
    }
  }

  /**
   * Load a tag back into path components
   */
  loadTag(tag: DataTag): (string | number)[] {
    return (
      tag.pathComponents ||
      tag.path.split('.').map((part) => {
        const num = parseInt(part)
        return isNaN(num) ? part : num
      })
    )
  }

  /**
   * Get value by flattened path
   */
  getValue(path: string): any {
    return this.flattenedData[path]
  }
}

export class DynamicTaggingForm {
  private pathBuilder: DynamicPathBuilder
  public currentPath: (string | number)[]
  public formLevels: FormLevel[]

  constructor(pathBuilder: DynamicPathBuilder) {
    this.pathBuilder = pathBuilder
    this.currentPath = []
    this.formLevels = []
    this.initializeForm()
  }

  private initializeForm(): void {
    this.rebuildFormLevels()
  }

  private rebuildFormLevels(): void {
    this.formLevels = []
    let tempPath: (string | number)[] = []

    // Build form levels based on current path
    for (let i = 0; i <= this.currentPath.length; i++) {
      tempPath = this.currentPath.slice(0, i)
      const options = this.pathBuilder.getNextLevelOptions(tempPath)

      if (options.length > 0) {
        const selectedValue =
          this.currentPath[i] !== undefined ? this.currentPath[i] : null
        this.formLevels.push({
          level: i,
          label: this.getLevelLabel(tempPath, i),
          options: options,
          selectedValue: selectedValue,
          path: [...tempPath],
        })
      }
    }
  }

  private getLevelLabel(pathSoFar: (string | number)[], level: number): string {
    if (level === 0) return 'Section'

    const parentData = pathSoFar.reduce(
      (current: any, key: string | number) => {
        if (current === null || current === undefined) return null
        return current[key]
      },
      this.pathBuilder['data'],
    )

    if (Array.isArray(parentData)) {
      return `Select Item`
    }

    if (level === 1) return 'Field'
    if (level === 2) return 'Sub-field'

    return `Level ${level + 1}`
  }

  updateSelection(level: number, value: string | number): void {
    // Update current path up to this level
    this.currentPath = this.currentPath.slice(0, level)
    this.currentPath[level] = value

    // Remove any path components after this level
    this.currentPath = this.currentPath.slice(0, level + 1)

    // Rebuild form levels
    this.rebuildFormLevels()
  }

  canCreateTag(): boolean {
    return (
      this.currentPath.length > 0 &&
      this.pathBuilder.isFinalValue(this.currentPath)
    )
  }

  getCurrentTag(): DataTag | null {
    if (!this.canCreateTag()) return null
    return this.pathBuilder.createTag(this.currentPath)
  }

  loadExistingTag(tag: DataTag): void {
    this.currentPath = this.pathBuilder.loadTag(tag)
    this.rebuildFormLevels()
  }

  getCurrentPath(): string {
    return this.pathBuilder.buildPathString(this.currentPath)
  }

  getCurrentValue(): any {
    return this.pathBuilder.getFinalValue(this.currentPath)
  }

  clearForm(): void {
    this.currentPath = []
    this.rebuildFormLevels()
  }

  getFormLevels(): FormLevel[] {
    return this.formLevels
  }
}
